import { foldForSearch } from '@/i18n/format.ts'

/**
 * Ranking for the command palette.
 *
 * Kept apart from the component because the ranking is the only part that can be subtly
 * wrong: a palette that puts a fuzzy match above an exact one, or that reorders on every
 * keystroke, is worse than no palette at all — the muscle memory of "press `/`, type three
 * letters, press Enter" only forms if the same three letters always land on the same row.
 *
 * The rules, in order:
 *   1. exact title match
 *   2. title starts with the query
 *   3. a word inside the title starts with the query
 *   4. the title contains the query anywhere
 * Ties break on the item's own position in its source list, which is already meaningful —
 * conversations arrive newest-first and agents arrive in the server's ranked order.
 *
 * Deliberately not fuzzy. Subsequence matching ("kb" finding "Knowledge Base") looks clever
 * in a demo and produces an unpredictable top result in daily use, which is the one thing a
 * keyboard shortcut cannot afford.
 */

/**
 * Which rail a result came from — a structural id, never the heading a reader sees.
 *
 * This used to be the rendered heading itself, doing four jobs at once: the group label, the
 * grouping key here, the React key and the icon-map key in `CommandPalette.tsx` (PRD-27 F-9).
 * Translating the heading would have made `GROUP_ICONS[item.group]` return `undefined` and
 * dropped every row back to a generic magnifier, with no type error, no runtime error and no
 * failing test. The display field is gone from this type entirely rather than merely joined
 * by an id, so the compiler — not a reviewer — finds any site that still reaches for it.
 */
export type PaletteGroupId = 'conversations' | 'agents' | 'knowledge' | 'groups' | 'destinations'

export type PaletteItem = {
  id: string
  /** The matched text. */
  title: string
  /** Second line: an owner, an agent name, a count. Not matched against. */
  detail?: string
  groupId: PaletteGroupId
  to: string
}

/**
 * Both sides are folded, so a Brazilian who types "servico" finds *Serviço* (PRD-27 D-26,
 * LOC-023). `Intl.Collator` settles comparison and sorting but cannot answer "does this
 * contain that", which is the only question a palette asks — so the folding is textual.
 * For an ASCII title and an ASCII query this is exactly the `toLowerCase()` it replaced.
 */
const score = (title: string, query: string): number => {
  const haystack = foldForSearch(title)
  if (haystack === query) return 0
  if (haystack.startsWith(query)) return 1
  // `\b` would not fire after a hyphen or a slash, which is where half of these titles break.
  if (haystack.split(/[\s\-_/·:,.]+/).some((word) => word.startsWith(query))) return 2
  if (haystack.includes(query)) return 3
  return -1
}

export const rankPaletteItems = (items: PaletteItem[], rawQuery: string, limit = 12): PaletteItem[] => {
  const query = foldForSearch(rawQuery.trim())
  if (query === '') return items.slice(0, limit)

  return items
    .map((item, index) => ({ item, index, rank: score(item.title, query) }))
    .filter((entry) => entry.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, limit)
    .map((entry) => entry.item)
}

/**
 * Group the ranked list for display without disturbing its order.
 *
 * Groups appear in the order their best result did, so the single most relevant row is always
 * in the first group — sorting groups by a fixed precedence instead would push the top match
 * down the list whenever it happened to be a conversation rather than an agent.
 */
export const groupPaletteItems = (items: PaletteItem[]): Array<{ groupId: PaletteGroupId; items: PaletteItem[] }> => {
  const groups: Array<{ groupId: PaletteGroupId; items: PaletteItem[] }> = []
  for (const item of items) {
    const existing = groups.find((entry) => entry.groupId === item.groupId)
    if (existing) existing.items.push(item)
    else groups.push({ groupId: item.groupId, items: [item] })
  }
  return groups
}
